/**
 * コンパクトダウンロードバッジ
 *
 * グリッドカード左下に表示する小さなダウンロード状態アイコン。
 * done / downloading / 中断中 / 画質変換中 / idle の状態を表現する。
 */
import { Clock, CloudOff, Download, Loader2, Pause, RotateCw, Settings2, Trash2, X } from 'lucide-react'

import { formatBytes } from '@/lib/format-utils'
import { cn } from '@/lib/utils'
import type { DownloadStatus } from '@/store/useDownloadStore'

type Props = {
  status: DownloadStatus
  progress?: number
  /** 中断中ダウンロードの進捗率（中断していなければ null） */
  resumePercent?: number | null
  /** ダウンロード前の画質変換の進捗率（変換していなければ null） */
  conversionPercent?: number | null
  /** ダウンロード済みファイルのサイズ（未取得・複数エピソード集計などで不明なら null） */
  downloadedSize?: number | null
  onDownload: (e: React.MouseEvent) => void
  onRemove: (e: React.MouseEvent) => void
  /** 進行中ダウンロードの一時停止 */
  onPause?: (e: React.MouseEvent) => void
  /** 中断中ダウンロードのクリック（未指定なら onDownload でそのまま再開） */
  onResume?: (e: React.MouseEvent) => void
  /** 画質変換待ちの中止 */
  onCancelConversion?: (e: React.MouseEvent) => void
  /** 追加のCSSクラス（z-10 等のレイアウト制御用） */
  className?: string
}

export const CompactDownloadBadge = ({
  status,
  progress,
  resumePercent = null,
  conversionPercent = null,
  downloadedSize = null,
  onDownload,
  onRemove,
  onPause,
  onResume,
  onCancelConversion,
  className,
}: Props) => {
  // 変換待ち：ダウンロード開始前の準備段階なので最優先で表示する
  if (conversionPercent !== null) {
    return (
      <div
        className={cn(
          'absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-violet-600/80 text-white px-1.5 py-0.5 group',
          onCancelConversion && 'cursor-pointer hover:bg-red-600/80 transition-colors',
          className,
        )}
        onClick={onCancelConversion}
        title={
          onCancelConversion
            ? `画質を変換中 ${conversionPercent}%（クリックで中止）`
            : `画質を変換中 ${conversionPercent}%`
        }
      >
        <Settings2 size={12} className={cn('animate-spin', onCancelConversion && 'group-hover:hidden')} />
        {onCancelConversion && <X size={12} className="hidden group-hover:block" />}
        <span className="text-[9px] font-bold leading-none">{conversionPercent}%</span>
      </div>
    )
  }

  if (status === 'done') {
    const sizeLabel = downloadedSize !== null ? formatBytes(downloadedSize) : null
    return (
      <div
        className={cn(
          'absolute bottom-1 left-1 rounded-full bg-green-600/80 text-white cursor-pointer hover:bg-red-600/80 transition-colors group',
          sizeLabel ? 'flex items-center gap-0.5 px-1.5 py-0.5' : 'p-1',
          className,
        )}
        onClick={onRemove}
        title={sizeLabel ? `オフライン保存済み ${sizeLabel}（クリックで削除）` : 'オフライン保存済み（クリックで削除）'}
      >
        <CloudOff size={12} className="group-hover:hidden" />
        <Trash2 size={12} className="hidden group-hover:block" />
        {sizeLabel && <span className="text-[9px] font-bold leading-none whitespace-nowrap">{sizeLabel}</span>}
      </div>
    )
  }

  if (status === 'queued') {
    // 直列キューで順番待ち
    return (
      <div
        className={cn(
          'absolute bottom-1 left-1 p-1 rounded-full bg-zinc-600/90 text-white group',
          onPause && 'cursor-pointer hover:bg-red-600/80 transition-colors',
          className,
        )}
        onClick={onPause}
        title={onPause ? 'ダウンロード待ち（クリックで取り消し）' : 'ダウンロード待ち'}
      >
        <Clock size={12} className={cn(onPause && 'group-hover:hidden')} />
        {onPause && <X size={12} className="hidden group-hover:block" />}
      </div>
    )
  }

  if (status === 'downloading' || status === 'saving') {
    const pct = progress ?? 0
    return (
      <div
        className={cn(
          'absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-blue-600/80 text-white px-1.5 py-0.5 group',
          onPause && 'cursor-pointer hover:bg-amber-600/90 transition-colors',
          className,
        )}
        onClick={onPause}
        title={onPause ? `ダウンロード中 ${pct}%（クリックで一時停止）` : `ダウンロード中 ${pct}%`}
      >
        <Loader2 size={12} className={cn('animate-spin', onPause && 'group-hover:hidden')} />
        {onPause && <Pause size={12} className="hidden group-hover:block" />}
        <span className="text-[9px] font-bold leading-none">{pct}%</span>
      </div>
    )
  }

  // 中断中：再開位置を % で示す
  if (resumePercent !== null) {
    return (
      <div
        className={cn(
          'absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-amber-600/90 text-white px-1.5 py-0.5 cursor-pointer hover:bg-amber-500 transition-colors',
          className,
        )}
        onClick={onResume ?? onDownload}
        title={`中断中 ${resumePercent}%（クリックで再開または削除）`}
      >
        <RotateCw size={12} />
        <span className="text-[9px] font-bold leading-none">{resumePercent}%</span>
      </div>
    )
  }

  // idle / error
  return (
    <div
      className={cn(
        'absolute bottom-1 left-1 p-1 rounded-full bg-zinc-700/80 text-zinc-300 cursor-pointer hover:bg-blue-600/80 hover:text-white transition-colors',
        className,
      )}
      onClick={onDownload}
      title="オフラインにダウンロード"
    >
      <Download size={12} />
    </div>
  )
}
